// Comprehensive plan data for technician presentations
// Includes features, benefits, covered components, and terms for all three companies

export interface PlanFeatureDetail {
  text: string;
  highlight?: boolean;
  tooltip?: string;
}

export interface PlanCoverage {
  hvacRepair?: string;
  plumbingRepair?: string;
  applianceRepair?: string;
  waterHeater?: string;
  replacementCredit?: string;
  diagnosticFee?: string;
  plumbingDiagnostic?: string;
  applianceDiagnostic?: string;
}

export interface CoveredComponent {
  name: string;
  included: boolean;
}

export interface PlanDetailData {
  code: string;
  name: string;
  shortDescription: string;
  tagline: string;
  features: PlanFeatureDetail[];
  coverage: PlanCoverage;
  coveredComponents: CoveredComponent[];
  talkingPoint: string;
}

// Hales AC Plans (2 tiers) - HVAC ONLY
// Updated January 2026
export const HALES_PLAN_DETAILS: PlanDetailData[] = [
  {
    code: 'standard',
    name: 'Comfort Club',
    shortDescription: 'Maintenance & savings for every homeowner',
    tagline: 'The Foundation',
    features: [
      { text: '1 or 2 Yearly Premier Preventive Maintenance & Tune Up' },
      { text: '12-Month Drain Pan Treatment included' },
      { text: '$69 Reduced Diagnostic Fee (save $30)', highlight: true },
      { text: '20% off all HVAC repairs' },
      { text: '20% off duct cleanings, filters, UV lights & IAQ' },
      { text: 'Priority, front-of-the-line scheduling' },
      { text: 'No after-hours or weekend fees' },
      { text: '$100/year Loyalty Credit toward new equipment (max $1,000)' },
      { text: '100% Guarantee on repairs for 1 year' },
    ],
    coverage: {
      hvacRepair: '20% off',
      diagnosticFee: '$69 (reduced from $99)',
      replacementCredit: '$100/year loyalty credit (max $1,000)',
    },
    coveredComponents: [],
    talkingPoint: 'For less than $17 a month, you get your annual tune-up, 20% off every repair, and priority scheduling. If your AC breaks down on a Saturday night, you\'re not paying extra to get us there.',
  },
  {
    code: 'premier',
    name: 'Premier Comfort Club',
    shortDescription: 'Best value with HVAC repair protection',
    tagline: 'The Sweet Spot',
    features: [
      { text: 'Everything in Comfort Club PLUS:', highlight: true },
      { text: '2 Maintenance Visits per year (Spring & Fall) - ALWAYS', highlight: true },
      { text: '$0 HVAC Diagnostic Fee', highlight: true },
      { text: 'Up to $1,500/yr in covered HVAC repairs', highlight: true },
      { text: 'Up to $1,500 Replacement Credit if unrepairable' },
      { text: '24/7/365 Emergency HVAC Service Access' },
      { text: 'No after-hours or weekend fees' },
      { text: '$100/year Loyalty Credit toward new equipment (max $1,000)' },
    ],
    coverage: {
      hvacRepair: 'Up to $1,500/year',
      diagnosticFee: '$0 (FREE)',
      replacementCredit: 'Up to $1,500',
    },
    coveredComponents: [
      { name: 'Compressor', included: true },
      { name: 'Motors', included: true },
      { name: 'Contactors & Capacitors', included: true },
      { name: 'Circuit Boards', included: true },
      { name: 'Relays', included: true },
      { name: 'Drain Pans', included: true },
      { name: 'Fan Blades', included: true },
    ],
    talkingPoint: 'This is our most popular plan. You get everything in Standard, plus up to $1,500 a year in repair coverage. That means when something breaks, we fix it - you don\'t pay out of pocket. Most repairs are covered completely. Premier always includes 2 maintenance visits per year.',
  },
];

// CCS & Abraham AC Plans (3 tiers - shared structure)
// Updated January 2026
export const CCS_ABRAHAM_PLAN_DETAILS: PlanDetailData[] = [
  {
    code: 'standard',
    name: 'Comfort Club',
    shortDescription: 'Maintenance & savings for every homeowner',
    tagline: 'The Foundation',
    features: [
      { text: '1 or 2 Yearly Premier Preventive Maintenance & Tune Up' },
      { text: '12-Month Drain Pan Treatment included' },
      { text: '$69 HVAC Diagnostic Fee (save $30)', highlight: true },
      { text: '$69 Plumbing & Appliance Diagnostic Fee' },
      { text: '20% off all repairs (HVAC, plumbing, appliance)' },
      { text: '20% off duct cleanings, filters, UV lights & IAQ' },
      { text: 'Priority, front-of-the-line scheduling' },
      { text: 'No after-hours or weekend service fees' },
      { text: '100% Guarantee on repairs for 1 year' },
      { text: '$100/year Loyalty Credit toward new equipment (max $1,000)' },
    ],
    coverage: {
      hvacRepair: '20% off',
      plumbingRepair: '20% off',
      applianceRepair: '20% off',
      diagnosticFee: '$69 (reduced from $99)',
      plumbingDiagnostic: '$69',
      applianceDiagnostic: '$69',
    },
    coveredComponents: [],
    talkingPoint: 'For less than $17 a month, you get your annual tune-up, 20% off every repair, and priority scheduling. If your AC breaks down on a Saturday night, you\'re not paying extra to get us there.',
  },
  {
    code: 'premier',
    name: 'Premier Comfort Club',
    shortDescription: 'Best value with HVAC, plumbing & water heater protection',
    tagline: 'The Sweet Spot',
    features: [
      { text: 'Everything in Comfort Club PLUS:', highlight: true },
      { text: '$0 HVAC Diagnostic Fee', highlight: true },
      { text: '$0 Plumbing Diagnostic Fee', highlight: true },
      { text: '$69 Appliance Diagnostic Fee (reduced)' },
      { text: 'Up to $1,500/yr HVAC Repair Coverage', highlight: true },
      { text: 'Up to $1,500/yr Plumbing Repair Coverage' },
      { text: 'Up to $500/yr Water Heater Repair Coverage' },
      { text: 'Minor plumbing repairs included (faucets, toilets, drains, etc.)' },
      { text: 'Up to $1,500 AC Replacement Credit' },
      { text: 'Up to $500 Water Heater Replacement Credit' },
      { text: '24/7/365 Emergency HVAC Service Access' },
    ],
    coverage: {
      hvacRepair: 'Up to $1,500/year',
      plumbingRepair: 'Up to $1,500/year',
      waterHeater: 'Up to $500/year',
      applianceRepair: '20% off',
      diagnosticFee: '$0 (FREE)',
      plumbingDiagnostic: '$0 (FREE)',
      applianceDiagnostic: '$69 (reduced)',
      replacementCredit: 'Up to $1,500 HVAC / Up to $500 Water Heater',
    },
    coveredComponents: [
      { name: 'Compressor', included: true },
      { name: 'Motors', included: true },
      { name: 'Contactors & Capacitors', included: true },
      { name: 'Circuit Boards', included: true },
      { name: 'Relays', included: true },
      { name: 'Drain Pans', included: true },
      { name: 'Fan Blades', included: true },
    ],
    talkingPoint: 'This is our most popular plan. You get everything in Standard, plus up to $1,500 a year in HVAC and plumbing repair coverage. Free diagnostics for HVAC and plumbing, plus minor plumbing repairs are included. Most repairs are covered completely.',
  },
  {
    code: 'optimum',
    name: 'Optimum Comfort Club',
    shortDescription: 'Complete home protection - HVAC, plumbing, & appliances',
    tagline: 'Complete Protection',
    features: [
      { text: 'Everything in Premier Comfort Club PLUS:', highlight: true },
      { text: '$0 Diagnostic Fee for HVAC, Plumbing, AND Appliances', highlight: true },
      { text: 'Standard Appliance Repair Coverage:', highlight: true },
      { text: '  • Refrigerator: up to $500/yr' },
      { text: '  • Oven/Range: up to $500/yr' },
      { text: '  • Washer: up to $500/yr' },
      { text: '  • Dryer: up to $500/yr' },
      { text: '  • Dishwasher: up to $300/yr' },
      { text: '  • Built-in Microwave: up to $200/yr' },
      { text: 'Minor plumbing repairs included' },
      { text: 'Drain line rooting/clearing included' },
      { text: '50% off premium appliance/plumbing repairs' },
      { text: '30% off duct cleanings, filters & IAQ (vs 20%)' },
      { text: 'Up to $3,000 AC Replacement Credit', highlight: true },
      { text: 'Enhanced replacement credits for all appliances' },
    ],
    coverage: {
      hvacRepair: 'Up to $1,500/year',
      plumbingRepair: 'Up to $1,500/year',
      applianceRepair: 'Up to $500/yr per major appliance',
      waterHeater: 'Up to $500/year',
      diagnosticFee: '$0 (FREE)',
      plumbingDiagnostic: '$0 (FREE)',
      applianceDiagnostic: '$0 (FREE)',
      replacementCredit: 'Up to $3,000 HVAC',
    },
    coveredComponents: [
      { name: 'Compressor', included: true },
      { name: 'Motors', included: true },
      { name: 'All HVAC Components', included: true },
      { name: 'Water Heater (≤50 gal electric tank)', included: true },
      { name: 'Minor Plumbing Fixtures', included: true },
      { name: 'Refrigerator (up to $500/yr)', included: true },
      { name: 'Oven/Range (up to $500/yr)', included: true },
      { name: 'Dishwasher (up to $300/yr)', included: true },
      { name: 'Washer (up to $500/yr)', included: true },
      { name: 'Dryer (up to $500/yr)', included: true },
      { name: 'Built-in Microwave (up to $200/yr)', included: true },
      { name: 'Garbage Disposal (up to $250 replacement)', included: true },
    ],
    talkingPoint: 'This is total peace of mind. HVAC, plumbing, AND appliances - all covered. Your refrigerator goes out? Covered up to $500. Dishwasher? Covered up to $300. Plus you get $3,000 toward a new AC if yours can\'t be fixed. All diagnostic fees are FREE across the board.',
  },
];

// Terms and Conditions - displayed during enrollment
// Updated January 2026 to match official Comfort Club terms
export interface TermsAndConditions {
  title: string;
  items: string[];
  keyTerms?: string[];
}

// Base terms for CCS and Abraham AC (full service: HVAC, plumbing, appliances)
export const MEMBERSHIP_TERMS: TermsAndConditions = {
  title: 'Membership Terms & Conditions',
  keyTerms: [
    'This is an ANNUAL MEMBERSHIP (12-month term), NOT a month-to-month plan.',
    'If you choose monthly billing, you are paying the annual fee in 12 monthly installments.',
    'Once ANY membership benefit is used (discount, diagnostic waiver, maintenance visit, etc.), the FULL annual fee is earned and due.',
    'Memberships AUTOMATICALLY RENEW each year unless you cancel before the renewal deadline.',
    'To cancel, provide written notice (email, mail, or online form) or cancel by the same method you used to enroll, as required by Florida law.',
  ],
  items: [
    'This is an annual membership (12-month term), not a month-to-month plan.',
    'If you choose monthly billing, monthly payments are installments of the annual fee.',
    'Once any membership benefit is used (discount, diagnostic waiver, maintenance visit, repair allowance, etc.), the full annual fee for that year is earned and due.',
    'Memberships automatically renew unless you cancel before the renewal deadline (at least 3 business days before renewal date).',
    'Renewal reminder notices will be sent 30-60 days before the cancellation deadline as required by Florida Statutes § 501.165.',
    'You may cancel by email, mail, online form, or by the same method you used to enroll.',
    'Pre-existing conditions are not covered. Breakdowns within the first 30 days may be deemed pre-existing.',
    'All covered equipment must be in good working order at contract start.',
    'Repair coverage limits reset annually on your membership anniversary date.',
    'Coverage applies to residential systems only.',
    'Refrigerant is not covered and is billed at current market rates. R-22 systems will not be recharged.',
    'Extended repair warranty: up to 1 year on repairs performed during active membership.',
    'Loyalty credit: $100 per paid membership year toward qualifying HVAC replacement (max $1,000).',
    'See full terms and conditions for complete coverage details, limits, and exclusions.',
  ],
};

// Hales AC specific terms (HVAC ONLY - no plumbing or appliance coverage)
export const HALES_TERMS: TermsAndConditions = {
  title: 'Hales AC Membership Terms',
  keyTerms: [
    'HVAC SERVICES ONLY: Hales AC specializes exclusively in HVAC services. This membership does NOT include plumbing, water heater, or appliance coverage of any kind.',
    ...MEMBERSHIP_TERMS.keyTerms!,
  ],
  items: [
    'HVAC SERVICES ONLY: Hales AC is an HVAC-only company. This membership does NOT include plumbing, water heater, or appliance coverage.',
    'For whole-home coverage including plumbing and appliances, contact our sister companies Climate Control Services (Palm Beach) or Abraham AC (Broward).',
    'Premier Comfort Club ALWAYS includes 2 maintenance visits per year - there is no 1-visit Premier option.',
    ...MEMBERSHIP_TERMS.items,
  ],
};

// Abraham AC specific terms (mirrors CCS but with Abraham branding)
export const ABRAHAM_TERMS: TermsAndConditions = {
  title: 'Abraham AC Membership Terms',
  keyTerms: MEMBERSHIP_TERMS.keyTerms,
  items: [
    'Abraham AC is a division of Climate Control Services, Inc., serving Broward County since 2003.',
    ...MEMBERSHIP_TERMS.items,
  ],
};

// Helper to get plan details by company
export function getPlanDetailsByCompany(companyId: string): PlanDetailData[] {
  if (companyId === 'hales-ac' || companyId === 'halesac') {
    return HALES_PLAN_DETAILS;
  }
  return CCS_ABRAHAM_PLAN_DETAILS;
}

// Helper to get terms by company
export function getTermsByCompany(companyId: string): TermsAndConditions {
  if (companyId === 'hales-ac' || companyId === 'halesac') {
    return HALES_TERMS;
  }
  if (companyId === 'abraham-ac' || companyId === 'abrahamac') {
    return ABRAHAM_TERMS;
  }
  return MEMBERSHIP_TERMS;
}

